import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useWishlist, type WishlistItem } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";

export default function WishlistDrawer() {
  const { wishlistItems, wishlistCount, removeFromWishlist, clearWishlist, isOpen, closeWishlist } = useWishlist();
  const { addItem } = useCart();

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") closeWishlist(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, closeWishlist]);

  const handleAddToCart = (item: WishlistItem) => {
    addItem({
      id: item.id,
      variantId: item.variantId,
      name: item.name,
      brand: item.brand,
      price: item.price,
      image: item.image,
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
        onClick={closeWishlist}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white z-50 flex flex-col shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Wishlist"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center bg-rose-50 rounded-lg">
              <i className="ri-heart-fill text-rose-500"></i>
            </div>
            <div>
              <h2 className="font-extrabold text-gray-900 text-base leading-tight">Saved Items</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {wishlistCount === 0
                  ? "Your wishlist is empty"
                  : `${wishlistCount} item${wishlistCount !== 1 ? "s" : ""} saved`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {wishlistCount > 0 && (
              <button
                onClick={clearWishlist}
                className="text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap px-2 py-1 rounded hover:bg-red-50"
              >
                Clear all
              </button>
            )}
            <button
              onClick={closeWishlist}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              aria-label="Close wishlist"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {wishlistCount === 0 ? (
            <EmptyState onClose={closeWishlist} />
          ) : (
            <div className="divide-y divide-gray-50">
              {wishlistItems.map((item) => (
                <WishlistItemRow
                  key={item.id}
                  item={item}
                  onRemove={removeFromWishlist}
                  onAddToCart={handleAddToCart}
                  onClose={closeWishlist}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer — only shown when there are items */}
        {wishlistCount > 0 && (
          <div className="border-t border-gray-100 px-5 py-4 shrink-0 space-y-3">
            <Link
              to="/shop"
              onClick={closeWishlist}
              className="flex items-center justify-center gap-2 w-full border-2 border-gray-200 hover:border-amber-400 text-gray-700 hover:text-amber-600 font-semibold text-sm py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-store-2-line"></i>
              </div>
              Continue Shopping
            </Link>
            <p className="text-center text-[11px] text-gray-400 leading-relaxed">
              Saved items are stored in your browser. Sign up for price drop alerts.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

function WishlistItemRow({
  item,
  onRemove,
  onAddToCart,
  onClose,
}: {
  item: WishlistItem;
  onRemove: (id: string) => void;
  onAddToCart: (item: WishlistItem) => void;
  onClose: () => void;
}) {
  const savePct = item.originalPrice
    ? Math.round((1 - item.price / item.originalPrice) * 100)
    : null;

  return (
    <div className="flex gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors group">
      {/* Image */}
      <Link
        to={`/shop/${item.id}`}
        onClick={onClose}
        className="w-20 h-20 shrink-0 bg-gray-50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center cursor-pointer"
      >
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-200"
        />
      </Link>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-0.5">{item.brand}</p>
        <Link
          to={`/shop/${item.id}`}
          onClick={onClose}
          className="text-sm font-bold text-gray-900 hover:text-amber-600 transition-colors cursor-pointer leading-snug line-clamp-2 block"
        >
          {item.name}
        </Link>

        {/* Price */}
        <div className="flex items-baseline gap-2 mt-1.5">
          {item.originalPrice ? (
            <>
              <span className="font-extrabold text-red-600 text-base">${item.price.toLocaleString()}</span>
              <span className="text-xs text-gray-400 line-through">${item.originalPrice.toLocaleString()}</span>
              {savePct && (
                <span className="text-[10px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">{savePct}% off</span>
              )}
            </>
          ) : (
            <span className="font-extrabold text-gray-900 text-base">${item.price.toLocaleString()}</span>
          )}
        </div>

        {/* Stock status */}
        <div className={`flex items-center gap-1 mt-1 text-[11px] font-semibold ${item.inStock ? "text-emerald-600" : "text-red-500"}`}>
          <div className="w-3 h-3 flex items-center justify-center">
            <i className={item.inStock ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"}></i>
          </div>
          {item.inStock ? "In Stock" : "Out of Stock"}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-2.5">
          <button
            onClick={() => item.inStock && onAddToCart(item)}
            disabled={!item.inStock}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              item.inStock
                ? "bg-amber-500 hover:bg-amber-400 text-white"
                : "bg-gray-100 text-gray-300 cursor-not-allowed"
            }`}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-shopping-cart-line"></i>
            </div>
            {item.inStock ? "Add to Cart" : "Unavailable"}
          </button>
          <button
            onClick={() => onRemove(item.id)}
            className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
            aria-label="Remove from wishlist"
          >
            <i className="ri-delete-bin-line text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
      <div className="w-20 h-20 flex items-center justify-center bg-rose-50 rounded-full mb-5">
        <i className="ri-heart-line text-4xl text-rose-300"></i>
      </div>
      <h3 className="text-gray-900 font-extrabold text-lg mb-2">Nothing saved yet</h3>
      <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
        Hit the heart on any product to save it here. Your list stays across sessions so you never lose track of what caught your eye.
      </p>
      <Link
        to="/shop"
        onClick={onClose}
        className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm px-6 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-store-2-line"></i>
        </div>
        Browse Products
      </Link>
    </div>
  );
}
